import { getCpmForCampaignType } from './cpmHelper';
import { campaignTypeMap } from '../constants/constants';

/**
 * Pricing Field Mapper Utility
 *
 * Maps between single UI CPM field and backend pricing fields based on advertiser's pricing model.
 * CRITICAL: Always reads from advertiserCPM/advertiserPercentage (ignores hasContract flag)
 * CRITICAL: Always DUPLICATES values to both non-contract AND contract fields when saving
 */

/**
 * Read pricing value for display in form based on advertiser's pricing model
 * Delegates to cpmHelper for consistent logic across new and existing campaigns
 *
 * @param {Object} campaign - Campaign object with pricing fields and type
 * @param {Object} advertiser - Advertiser with pricingModel
 * @returns {number} - The pricing value to display in the CPM input field
 */
export const getPricingValueForDisplay = (campaign, advertiser) => {
  // Delegate to cpmHelper for consistent reading logic
  // cpmHelper already handles: campaign type, percentage model, fallbacks
  return getCpmForCampaignType(advertiser, campaign?.type);
};

/**
 * Get pricing field object for saving based on pricing model
 * CRITICAL: Copies BOTH values from advertiser (advertiserCPM AND contractCPM)
 *
 * @param {number} cpmValue - Value from the CPM input field (unused - we read from advertiser)
 * @param {Object} advertiser - Advertiser with pricingModel
 * @param {string|number} campaignType - Campaign type (DISPLAY, VIDEO, EXTERNAL, etc.)
 * @param {number|null} percentageOverride - Optional percentage override value for Percentage model
 * @returns {Object} - Object with all 4 pricing fields (cpm + contractCPM from advertiser)
 */
export const getPricingFieldsForSave = (cpmValue, advertiser, campaignType, percentageOverride = null) => {
  const pricingModel = advertiser?.pricingModel?.model;

  // Initialize all to null
  const fields = {
    cpm: null,
    contractCPM: null,
    advertiserPercentage: null,
    contractPercentage: null
  };

  // Percentage model - Copy BOTH percentage values from advertiser
  if (pricingModel === 'Percentage') {
    const auctionPlus = advertiser?.pricingModel?.auctionPlus;

    // Use override if provided, otherwise use advertiser default
    if (percentageOverride !== null && percentageOverride !== undefined) {
      const parsedOverride = parseFloat(String(percentageOverride).replace(/,/g, ''));
      fields.advertiserPercentage = parsedOverride;
      fields.contractPercentage = parsedOverride;
    } else if (auctionPlus) {
      fields.advertiserPercentage = auctionPlus.advertiserPercentage
        ? parseFloat(String(auctionPlus.advertiserPercentage).replace(/,/g, ''))
        : null;
      fields.contractPercentage = auctionPlus.contractPercentage
        ? parseFloat(String(auctionPlus.contractPercentage).replace(/,/g, ''))
        : null;
    }

    return fields;
  }

  // CPM model (default) - Copy BOTH CPM values from advertiser for the campaign type
  if (pricingModel === 'CPM' && advertiser?.pricingModel?.cpmList?.length > 0) {
    const { cpmList } = advertiser.pricingModel;

    // Convert campaign type to string if needed
    const typeString = typeof campaignType === 'number'
      ? campaignTypeMap[campaignType]
      : campaignType;

    // Find matching CPM entry for this campaign type
    const match = cpmList.find(item => item.type === typeString);

    if (match) {
      // Copy BOTH advertiserCPM and contractCPM from the advertiser
      fields.cpm = match.advertiserCPM
        ? parseFloat(String(match.advertiserCPM).replace(/,/g, ''))
        : null;
      fields.contractCPM = match.contractCPM
        ? parseFloat(String(match.contractCPM).replace(/,/g, ''))
        : null;
    } else {
      // Fallback: use first entry in cpmList
      const firstEntry = cpmList[0];
      fields.cpm = firstEntry.advertiserCPM
        ? parseFloat(String(firstEntry.advertiserCPM).replace(/,/g, ''))
        : null;
      fields.contractCPM = firstEntry.contractCPM
        ? parseFloat(String(firstEntry.contractCPM).replace(/,/g, ''))
        : null;
    }
  } else {
    // Legacy fallback: use the cpmValue from the form
    fields.cpm = cpmValue;
    fields.contractCPM = cpmValue;
  }

  return fields;
};
